/**
 * Vue composable for managing Pusher socket subscriptions
 * @param {Object} pusherInstance - Pusher instance with subscribe/unsubscribe methods
 * @param {Object.<string, Object.<string, Function>>} channels - Channel configuration object
 * @returns {Object} - Object with subscribeToChannels and unsubscribeFromChannels methods
 *
 * @example
 * subscribeToChannels(
 *   pusher,
 *   {
 *     'my-channel': {
 *       'new-message': (data) => console.log(data),
 *       'user-typing': (data) => handleTyping(data)
 *     }
 *   }
 * )
 */
export const applyChannels = (
  pusherInstance = {},
  channels = {}
) => {
  const { subscribe, unsubscribe } = pusherInstance

  if (!subscribe || !unsubscribe) {
    console.error('Invalid pusher instance provided to subscribeToChannels')
    return
  }

  const subscribeToChannels = () => {
    try {
      Object.entries(channels).forEach(([ channelName, events ]) => {
        if (!channelName || typeof events !== 'object') {
          console.warn(`Invalid channel configuration for: ${channelName}`)
          return
        }

        Object.entries(events).forEach(([ eventName, callback ]) => {
          if (!eventName || typeof callback !== 'function') {
            console.warn(`Invalid event configuration for ${channelName}:${eventName}`)
            return
          }
          subscribe(channelName, eventName, callback)
        })
      })
    } catch (error) {
      console.error('Error setting up socket subscriptions:', error)
    }
  }

  const unsubscribeFromChannels = () => {
    try {
      Object.keys(channels).forEach(channel => unsubscribe(channel))
    } catch (error) {
      console.error('Error cleaning up socket subscriptions:', error)
    }
  }

  return {
    subscribeToChannels,
    unsubscribeFromChannels
  }
}
